import loadScript from 'load-script';

import { addClass, removeClass } from 'common/tools/dom/classes';
import { on } from 'common/tools/dom/events';
import swipeOn, {
  SWIPE_BOTTOM,
  SWIPE_LEFT,
  SWIPE_RIGHT,
  SWIPE_TOP
} from 'common/tools/dom/swipe';

const SHORTS_CARD_SELECTOR = '.video-card-shorts';

const OVERLAY_CLASSNAME = 'shorts-overlay';
const STAGE_CLASSNAME = 'shorts-stage';
const PLAYER_ID = 'shorts-player';
const CAPTURE_CLASSNAME = 'shorts-overlay-capture';
const CLOSE_CLASSNAME = 'shorts-overlay-close';

const BODY_OPEN_CLASSNAME = 'shorts-overlay-open';

const CLOSE_ICON = 'close1';

const YOUTUBE_API_URL = 'https://www.youtube.com/player_api';

const PREVIOUS_KEYS = ['ArrowUp', 'ArrowLeft'];
const NEXT_KEYS = ['ArrowDown', 'ArrowRight'];
const TOGGLE_KEYS = [' ', 'Spacebar'];

// A single wheel gesture fires a burst of events: act on the first one, then stay deaf
// long enough for the burst to die out and the next short to start.
export const WHEEL_COOLDOWN_MS = 600;
// Below this, the movement is trackpad noise rather than an intentional gesture.
export const WHEEL_THRESHOLD_PX = 20;

// Firefox reports a physical mouse wheel in lines (±3 per notch), not pixels: the raw
// deltaY would never reach the threshold above. Both modes have to be converted first.
const DOM_DELTA_LINE = 1;
const DOM_DELTA_PAGE = 2;
const LINE_HEIGHT_PX = 16;

type Short = {
  videoId: string;
  title: string;
};

let shorts: Short[] = [];
let currentIndex = -1;

let overlay: HTMLElement | null = null;

let playerInstance: YouTube.Player | null = null;
let apiRequested = false;
// Set as soon as the constructor is called, whereas `playerInstance` only lands on onReady:
// between the two, nothing else must build a second player on the same anchor.
let playerRequested = false;
let pendingVideoId: string | null = null;

let isOpen = false;
let wheelLocked = false;
let lastFocused: HTMLElement | null = null;

const currentShort = () => shorts[currentIndex] ?? null;

const applyPlayerTitle = () => {
  const short = currentShort();

  if (!playerInstance || !short) {
    return;
  }

  playerInstance.getIframe().title = short.title;
};

/**
 * The feed wraps around in both directions, so a viewer who keeps scrolling never hits a
 * dead end. A one-short feed replays that short rather than freezing on its last frame:
 * `controls: 0` leaves no replay button, so the only way out would be to close and reopen.
 */
const goToOffset = (offset: number) => {
  if (!isOpen || !playerInstance || !shorts.length) {
    return;
  }

  currentIndex = (currentIndex + offset + shorts.length) % shorts.length;

  playerInstance.loadVideoById(shorts[currentIndex].videoId);
  applyPlayerTitle();
};

const togglePlayback = () => {
  if (!isOpen || !playerInstance || !window.YT) {
    return;
  }

  if (playerInstance.getPlayerState() === window.YT.PlayerState.PLAYING) {
    playerInstance.pauseVideo();

    return;
  }

  playerInstance.playVideo();
};

const close = () => {
  if (!isOpen) {
    return;
  }

  isOpen = false;

  // An API load may still be in flight: without this it would build a player, autoplay
  // included, behind an overlay that is now `display: none` and no longer reachable.
  pendingVideoId = null;

  removeClass(document.body, BODY_OPEN_CLASSNAME);

  playerInstance?.pauseVideo();

  // Hiding the overlay drops the focus onto <body>, which would send a keyboard viewer
  // back to the top of the document instead of the card they came from.
  lastFocused?.focus();
  lastFocused = null;
};

const lockWheel = () => {
  wheelLocked = true;

  window.setTimeout(() => {
    wheelLocked = false;
  }, WHEEL_COOLDOWN_MS);
};

const toPixels = (delta: number, deltaMode: number) => {
  if (deltaMode === DOM_DELTA_LINE) {
    return delta * LINE_HEIGHT_PX;
  }

  if (deltaMode === DOM_DELTA_PAGE) {
    return delta * window.innerHeight;
  }

  return delta;
};

const handleWheel = (event: Event) => {
  if (!isOpen) {
    return;
  }

  event.preventDefault();

  if (wheelLocked) {
    return;
  }

  const { deltaX, deltaY, deltaMode } = event as WheelEvent;
  const raw = Math.abs(deltaY) >= Math.abs(deltaX) ? deltaY : deltaX;
  const delta = toPixels(raw, deltaMode);

  if (Math.abs(delta) < WHEEL_THRESHOLD_PX) {
    return;
  }

  lockWheel();

  goToOffset(delta > 0 ? +1 : -1);
};

/**
 * stopPropagation, not just preventDefault: the homepage hero carousel listens for arrow
 * keys on `window`, with neither a focus nor an overlay guard. Our listener sits on
 * `document`, which bubbles first, so this is what keeps the slider behind from moving
 * along with the feed.
 */
const consumeKey = (event: Event) => {
  event.preventDefault();
  event.stopPropagation();
};

const handleKeydown = (event: Event) => {
  if (!isOpen) {
    return;
  }

  const { key } = event as KeyboardEvent;

  if (key === 'Escape') {
    consumeKey(event);
    close();

    return;
  }

  if (PREVIOUS_KEYS.includes(key)) {
    consumeKey(event);
    goToOffset(-1);

    return;
  }

  if (NEXT_KEYS.includes(key)) {
    consumeKey(event);
    goToOffset(+1);

    return;
  }

  if (TOGGLE_KEYS.includes(key)) {
    consumeKey(event);
    togglePlayback();
  }
};

const handleSwipe = (
  element: Element | undefined,
  direction: string | null
) => {
  if (element !== overlay) {
    return;
  }

  if (direction === SWIPE_BOTTOM || direction === SWIPE_RIGHT) {
    goToOffset(+1);

    return;
  }

  if (direction === SWIPE_TOP || direction === SWIPE_LEFT) {
    goToOffset(-1);
  }
};

/**
 * The YouTube iframe is cross-origin: it swallows every wheel and touch event happening
 * over its own surface. A transparent layer on top of it is the only way to let them
 * through to the overlay, and it takes over the play/pause the embed would otherwise
 * handle on click.
 */
const createCaptureLayer = (): HTMLElement => {
  const layer = document.createElement('div');

  layer.className = CAPTURE_CLASSNAME;

  on(layer, 'click', (event: Event) => {
    // Otherwise the click reaches the overlay backdrop, which closes everything.
    event.stopPropagation();
    togglePlayback();
  });

  return layer;
};

const createOverlay = () => {
  if (overlay) {
    return;
  }

  overlay = document.createElement('div');
  overlay.className = OVERLAY_CLASSNAME;
  overlay.setAttribute('role', 'dialog');
  overlay.setAttribute('aria-modal', 'true');
  // Focusable so the arrow keys reach us instead of the embed once the viewer clicks.
  overlay.tabIndex = 0;

  // On the overlay rather than the stage: the stage is a 9/16 box, about 30 % of a 16:9
  // viewport, and a wheel over the surrounding backdrop has to navigate too. `passive:
  // false` so preventDefault() really stops the page behind from scrolling.
  overlay.addEventListener('wheel', handleWheel, { passive: false });

  const stage = document.createElement('div');
  const anchor = document.createElement('div');
  const closeButton = document.createElement('div');

  stage.className = STAGE_CLASSNAME;
  anchor.id = PLAYER_ID;

  stage.append(anchor, createCaptureLayer());

  closeButton.className = `${CLOSE_CLASSNAME} ui-icons`;
  closeButton.textContent = CLOSE_ICON;

  overlay.append(stage, closeButton);

  on(overlay, 'click', close);

  document.body.appendChild(overlay);

  swipeOn(overlay, handleSwipe);
  on(document, 'keydown', handleKeydown);
};

const createPlayer = (videoId: string) => {
  if (!window.YT || playerRequested) {
    return;
  }

  playerRequested = true;

  new window.YT.Player(PLAYER_ID, {
    videoId: videoId,
    playerVars: {
      autoplay: 1,
      controls: 0,
      rel: 0,
      modestbranding: 1,
      // The previous embed set allowFullscreen on the iframe: keep fullscreen reachable
      // rather than inheriting the reference implementation's fs: 0.
      fs: 1,
      playsinline: 1
    },
    events: {
      onReady: event => {
        playerInstance = event.target;

        // Building the player takes hundreds of ms, during which the viewer may have
        // closed the overlay or moved on to another short.
        if (!isOpen) {
          playerInstance.pauseVideo();

          return;
        }

        const short = currentShort();

        if (short && short.videoId !== videoId) {
          playerInstance.loadVideoById(short.videoId);
        }

        applyPlayerTitle();
      },
      onStateChange: event => {
        if (event.data === window.YT?.PlayerState.ENDED) {
          goToOffset(+1);
        }
      }
    }
  });
};

const handleApiLoadError = (error?: Error) => {
  if (error) {
    // Let a later click retry instead of staying stuck on a failed load.
    apiRequested = false;
  }
};

const playShort = (videoId: string) => {
  if (playerInstance) {
    playerInstance.loadVideoById(videoId);
    applyPlayerTitle();

    return;
  }

  // A player is already on its way: its onReady picks up whichever short is current by then.
  if (playerRequested) {
    return;
  }

  if (window.YT?.Player) {
    createPlayer(videoId);

    return;
  }

  // The API script may already be on its way: this is the short its callback will open.
  pendingVideoId = videoId;

  if (apiRequested) {
    return;
  }

  apiRequested = true;

  // Registered before the script is requested, otherwise the API could call it back
  // before we had a chance to define it.
  window.onYouTubePlayerAPIReady = () => {
    // close() clears pendingVideoId: a viewer who gave up on a slow load must not get a
    // short autoplaying behind a hidden overlay, with every way out gated on `isOpen`.
    if (isOpen && pendingVideoId) {
      createPlayer(pendingVideoId);
    }
  };

  loadScript(YOUTUBE_API_URL, handleApiLoadError);
};

const open = (index: number, trigger: HTMLElement) => {
  createOverlay();

  currentIndex = index;
  isOpen = true;
  lastFocused = trigger;

  addClass(document.body, BODY_OPEN_CLASSNAME);

  playShort(shorts[index].videoId);

  overlay?.focus();
};

export default function shortsPlayer(): void {
  const cards = document.querySelectorAll<HTMLElement>(SHORTS_CARD_SELECTOR);

  if (!cards.length) {
    return;
  }

  shorts = [];

  cards.forEach(card => {
    const { videoId } = card.dataset;
    const link = card.querySelector('a');

    if (!videoId || !link) {
      return;
    }

    const index = shorts.length;

    shorts.push({ videoId, title: link.textContent?.trim() ?? '' });

    on(card, 'click', (event: Event) => {
      event.preventDefault();
      open(index, link);
    });
  });
}
